import { useCallback } from 'react';
import { differenceInCalendarDays, format } from 'date-fns';
import { useTranslation, useHistory } from '@shared/core';
import { useRoutePaths } from '@apps/registry/admin/Registry.routes';
import { useGetFinalizedDateAndGiftCountQuery } from '@graphql/generated';
import { GiftOptions, getCountDownRedirectionUrl } from '../AdminProductListActions/AdminProductListActionsController';
import { useAdminRegistryTelemetry } from '../../AdminRegistry.telemetry';

export type GiftCountControllerProps = {
  eventHandle: string;
  onGiftTypeClick?: (type: GiftOptions.STILLNEEDED | GiftOptions.RESERVEDANDPURCHASED) => void;
  page?: string;
};

export const useCountdownGiftCountControllerV2 = ({ eventHandle, onGiftTypeClick, page }: GiftCountControllerProps) => {
  const telemetry = useAdminRegistryTelemetry();
  const { t } = useTranslation('sharedComponents');
  const {
    greeting: { countdownToWedding, countdownPostWedding, weddingDay, dailyMessage },
  } = t('countdownGiftCount');

  const { data, loading } = useGetFinalizedDateAndGiftCountQuery({ variables: { eventHandle }, batchMode: 'fast' });

  const { push } = useHistory();
  const routes = useRoutePaths();

  const today = Date.now();
  const finalizedEventDate = data?.eventByName?.info.finalizedDate;
  let countDownValue: Maybe<string> = dailyMessage({ dayOfWeek: format(today, 'eeee') });
  if (finalizedEventDate) {
    const countDown = differenceInCalendarDays(finalizedEventDate.milliseconds, today);
    countDownValue = countdownToWedding({ count: Math.abs(countDown) });
    if (countDown === 0) {
      countDownValue = weddingDay({ count: Math.abs(countDown) });
    }
    if (countDown < 0) {
      countDownValue = countdownPostWedding({ count: Math.abs(countDown) });
    }
  }

  const handleOnGiftTypeClick = useCallback(
    (event: React.SyntheticEvent<HTMLDivElement>) => {
      const filterType = event.currentTarget.dataset.area;
      const giftOption = filterType === 'available' ? GiftOptions.STILLNEEDED : GiftOptions.RESERVEDANDPURCHASED;
      if (onGiftTypeClick) {
        onGiftTypeClick(giftOption);
      } else {
        telemetry.giftTypeClick(page || '', filterType);
        push(getCountDownRedirectionUrl(routes.manage.path, giftOption));
      }
    },
    [onGiftTypeClick, telemetry, page, push, routes.manage.path],
  );

  return {
    countDownValue,
    everything: data?.eventByName?.registry.itemCount.everything,
    purchased: data?.eventByName?.registry.itemCount.purchasedOrReserved,
    countDownValueLoading: loading,
    handleOnGiftTypeClick,
  };
};
